import React from 'react';
import { Button, Card } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { validateNumber } from 'avilatek-utils';
import { FormHeader } from '../../shared/Forms';
import { ClientGoals, Period, TPeriodForm } from '../../../models';
import { UPDATE_PROGRESS_CLIENT_GOAL } from '../../../graphql/mutation';
import { useNotify } from '../../../hooks';
import { Alert, FadeAnimation } from '../../shared';
import MetricCriterias from './MetricCriterias';
import EmployeeGoalInputs from './EmployeeGoalInputs';

interface EmployeeGoalFormProps {
  period: Partial<Period>;
  clientGoal: ClientGoals;
}

function EmployeeGoalForm({ period, clientGoal }: EmployeeGoalFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [firstResult, setFirstResult] = React.useState('');
  const { clientGoalId } = router.query;

  const [periodSelected, setPeriodSelected] = React.useState<Partial<Period>>({
    target: 0,
    criterias: [],
  });
  const [formState, setFormState] = React.useState<TPeriodForm>({
    target: '',
    criteria: [],
    result: '',
  });

  const [updateProgressClientGoal] = useMutation<{
    progressClientGoal: ClientGoals;
  }>(UPDATE_PROGRESS_CLIENT_GOAL);

  const updateProgress = async () => {
    // Por ahora lo mantengo comentado
    // if (parseInt(firstResult) >= parseInt(formState.result)) {
    //   notify(
    //     `El resultado no puede ser menor o igual que ${firstResult}`,
    //     'error'
    //   );
    //   return;
    // }
    try {
      const { data } = await updateProgressClientGoal({
        variables: {
          data: {
            clientGoalId,
            result: parseFloat(formState.result),
          },
        },
      });
      if (data?.progressClientGoal?._id) {
        notify('Progreso asignado con éxito', 'success');
        setTimeout(() => {
          router.push(`/app/periods/${period._id}`);
        }, 1000);
      } else {
        notify('Ha ocurrido un error al registrar el progreso', 'error');
      }
    } catch (error) {
      notify('Ha ocurrido un error al registrar el progreso', 'error');
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    updateProgress();
  };

  const progress = React.useMemo(() => {
    if (
      period &&
      validateNumber(formState.result) &&
      Number(formState.result) > 0
    ) {
      return (Number(formState.result) / period.target) * 100;
    }
    if (clientGoal && !period) {
      return (clientGoal.result / clientGoal.target) * 100;
    }

    return 0;
  }, [formState.result, period]);
  React.useEffect(() => {
    setFirstResult(clientGoal?.result.toString() ?? '');
    if (period) {
      setFormState({
        target: period.target,
        criteria: period.criterias,
        result: clientGoal?.result.toString() ?? '',
      });
      setPeriodSelected({
        target: period.target,
        criterias: period.criterias,
      });
    }
  }, [period, clientGoal]);

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Card>
        <FormHeader backBtnText="" title="Metas para beneficiarios" />
        <Card.Body>
          <EmployeeGoalInputs
            formState={formState}
            setFormState={setFormState}
          />
        </Card.Body>
      </Card>
      <Card>
        <FormHeader backBtnText="" title="Desempeño del beneficiario" />
        <Card.Body>
          {formState.target !== '' && formState.result !== '' ? (
            <FadeAnimation>
              <MetricCriterias
                goalSelected={periodSelected ?? clientGoal}
                progress={progress}
                criterias={
                  periodSelected?.criterias ?? clientGoal?.criteria ?? []
                }
              />
            </FadeAnimation>
          ) : (
            <Alert
              title="Indique el resultado del beneficiario"
              text="Una vez indicado el resultado podra visualizar el desempeño del beneficiario con su respectiva recompensa"
              show
            />
          )}
        </Card.Body>
      </Card>
      {formState.target !== '' && formState.result !== '' && (
        <div className="flex md:w-1/3 md:ml-auto">
          <Button type="submit">Guardar</Button>
        </div>
      )}
    </form>
  );
}

export default EmployeeGoalForm;
